
import { EOLCycleData, EOLData } from '@/components/eol/types';

/**
 * Process EOL data to get upcoming EOL items
 */
export function processEOLData(eolData: EOLData): Array<{
  name: string;
  cycle: string;
  eol: string;
  daysRemaining: number;
}> {
  const today = new Date();
  const upcomingEOL: Array<{
    name: string;
    cycle: string;
    eol: string;
    daysRemaining: number;
  }> = [];

  Object.entries(eolData).forEach(([name, cycles]) => {
    cycles.forEach((cycle: EOLCycleData) => {
      if (cycle.eol) {
        try {
          const eolDate = new Date(cycle.eol);
          if (!isNaN(eolDate.getTime())) {
            const daysRemaining = Math.ceil((eolDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            if (daysRemaining > 0 && daysRemaining <= 365) {
              upcomingEOL.push({
                name,
                cycle: cycle.cycle,
                eol: cycle.eol,
                daysRemaining
              });
            }
          }
        } catch (error) {
          console.warn(`Invalid EOL date for ${name} ${cycle.cycle}: ${cycle.eol}`);
        }
      }
    });
  });

  return upcomingEOL.sort((a, b) => a.daysRemaining - b.daysRemaining);
}

/**
 * Count EOL items expiring in the next 30 days
 */
/**
 * Count EOL items expiring in the next N days
 */
export function countUpcomingEOLItems(eolData: EOLData, days: number = 30): { current: number, previous: number } {
  const today = new Date();
  let currentCount = 0;
  let previousCount = 0; // For EOL, "previous" might mean items that expired in the *last* N days, or items expiring in N days *after* the current period. 
  // Given the context of "upcoming", let's compare with items expiring in the NEXT period (days to 2*days).
  // OR, maybe it makes more sense to compare with how many items were expiring in the *past* window?
  // The user wants "growth or drop". Usually for "upcoming events", growth means "more things are about to happen".
  // Let's stick to the standard "current period vs previous period" logic but applied to the future timeline if we treat "upcoming" as the metric.
  // Actually, usually "upcoming EOL" is a static list. Comparing it to "previous period" is tricky.
  // Let's define:
  // Current: Expiring in [Today, Today + Days]
  // Previous: Expiring in [Today + Days, Today + 2*Days] (This would mean "are there more expiring soon vs later?")
  // OR
  // Previous: Expired in [Today - Days, Today] (This compares upcoming with recently expired)

  // Let's go with: Current = [Today, Today + Days], Previous = [Today + Days, Today + 2*Days]
  // This tells us if the "wave" of EOLs is increasing or decreasing in the near future.

  Object.entries(eolData).forEach(([_, cycles]) => {
    cycles.forEach((cycle: EOLCycleData) => {
      if (cycle.eol) {
        try {
          const eolDate = new Date(cycle.eol);
          if (!isNaN(eolDate.getTime())) {
            const daysRemaining = Math.ceil((eolDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            // Current period: 0 to days
            if (daysRemaining > 0 && daysRemaining <= days) {
              currentCount++;
            }

            // "Previous" period (comparison): days to 2*days
            // This allows us to say "10 items expiring in 30 days, vs 5 items in the subsequent 30 days"
            // Wait, usually "trend" implies time moving forward.
            // If we want to show "growth", maybe we should compare with how many items expired in the LAST 30 days?
            // But the metric is "Upcoming EOL".
            // Let's stick to the [Today + Days, Today + 2*Days] as the "baseline" to compare against? 
            // No, that's weird. 
            // Let's try: How many items were "upcoming" 30 days ago? We can't know that easily without historical data.

            // Alternative: Compare [Today, Today+Days] vs [Today-Days, Today].
            // i.e. "5 items expiring soon" vs "3 items expired recently".
            // This gives a sense of "turnover".

            if (daysRemaining <= 0 && daysRemaining >= -days) {
              previousCount++;
            }
          }
        } catch (error) {
          // Skip invalid dates
        }
      }
    });
  });

  return { current: currentCount, previous: previousCount };
}
